<template>
  <field-frame :field="field" :show-optional="showOptional">
    <template #default="{ inputId, describedBy }">
      <!-- painscale: VAS face scale (旧版踏襲)
           SVG 画像 + 数字位置 (画像下部) に 11 個の半透明白ボックスを overlay -->
      <div v-if="field.subtype === 'painscale'" class="space-y-2">
        <div class="relative w-full max-w-2xl mx-auto painscale-wrap">
          <img
            :src="field.img || painScaleSvg"
            class="w-full block"
            :alt="$t('痛みスケール')"
          />
          <div
            v-for="n in 11"
            :key="n"
            class="painscale-box"
            :class="{ 'painscale-box-active': field.value === n - 1 }"
            :style="painscaleBoxStyle(n - 1)"
            aria-hidden="true"
          ></div>
        </div>
        <div class="flex items-center justify-between text-xs text-q4cl-muted px-1">
          <span v-if="field.starttag">{{ $t(field.starttag) }} (0)</span>
          <span v-if="field.endtag" class="ml-auto">(10) {{ $t(field.endtag) }}</span>
        </div>
        <!-- 旧版踏襲 subItemActivated: 初期は touch-block で誤タップ防止 -->
        <div class="flex items-center gap-3 scale-row">
          <div class="relative flex-1">
            <div ref="singleSliderRef" :id="inputId" class="single-slider" :aria-describedby="describedBy"></div>
            <div
              v-if="!activated"
              class="scale-activate-overlay"
              role="button"
              tabindex="0"
              :aria-label="$t('タップして操作可能にする')"
              @click="activate"
              @keydown.enter.prevent="activate"
              @keydown.space.prevent="activate"
            >
              <span class="scale-activate-hint">{{ $t('タップして操作') }}</span>
            </div>
          </div>
          <div class="text-base font-bold text-q4cl-primary min-w-[5rem] text-right" aria-hidden="true">
            {{ displayValue }}
          </div>
        </div>
      </div>

      <!-- 通常スケール / bodytemp -->
      <div v-else-if="field.subtype !== 'bloodpressure'" class="space-y-2">
        <div v-if="field.starttag || field.endtag" class="flex items-center justify-between text-xs text-q4cl-muted">
          <span v-if="field.starttag">{{ $t(field.starttag) }}</span>
          <span v-if="field.endtag" class="ml-auto">{{ $t(field.endtag) }}</span>
        </div>
        <div class="flex items-center gap-3 scale-row">
          <div class="relative flex-1">
            <div ref="singleSliderRef" :id="inputId" class="single-slider" :aria-describedby="describedBy"></div>
            <div
              v-if="!activated"
              class="scale-activate-overlay"
              role="button"
              tabindex="0"
              :aria-label="$t('タップして操作可能にする')"
              @click="activate"
              @keydown.enter.prevent="activate"
              @keydown.space.prevent="activate"
            >
              <span class="scale-activate-hint">{{ $t('タップして操作') }}</span>
            </div>
          </div>
          <div class="text-base font-bold text-q4cl-primary min-w-[5rem] text-right" aria-hidden="true">
            {{ displayValue }}
          </div>
        </div>
      </div>

      <!-- bloodpressure: noUiSlider デュアルハンドル -->
      <div v-if="field.subtype === 'bloodpressure'" class="space-y-3">
        <div class="flex items-center justify-between text-sm text-q4cl-muted">
          <span>{{ $t('下 (最低血圧)') }}: <strong class="text-q4cl-primary">{{ field.value.lower || '—' }}</strong> mmHg</span>
          <span>{{ $t('上 (最高血圧)') }}: <strong class="text-q4cl-primary">{{ field.value.upper || '—' }}</strong> mmHg</span>
        </div>
        <div class="relative">
          <div ref="bpSliderRef" class="bp-slider" :aria-describedby="describedBy"></div>
          <div
            v-if="!activated"
            class="scale-activate-overlay"
            role="button"
            tabindex="0"
            :aria-label="$t('タップして操作可能にする')"
            @click="activate"
            @keydown.enter.prevent="activate"
            @keydown.space.prevent="activate"
          >
            <span class="scale-activate-hint">{{ $t('タップして操作') }}</span>
          </div>
        </div>
      </div>
    </template>
  </field-frame>
</template>

<script>
import FieldFrame from '@/components/ui/FieldFrame.vue'
import painScaleSvg from '@/assets/PainScale.svg'
/**
 * ScaleField: 全スライダーを noUiSlider で統一実装。
 *  - シングルモード (painscale / bodytemp / 通常 scale): start[1値], connect: 'lower'
 *  - デュアルモード (bloodpressure): start[2値], connect: true
 * 進捗バー (sky-600 connect) と背景バー (slate-200 .noUi-target) が完全に統一される。
 */
export default {
  name: "ScaleField",
  components: {
    'field-frame': FieldFrame,
  },
  props: {
    field: { type: Object, required: true },
    showOptional: { type: Boolean, default: false }
  },
  setup(props) {
    const sub = Vue.computed(() => props.field.subtype || "scale");

    // 旧旧版踏襲 (RenderQuestionnaire.vue:253,254 subItemActivated): 誤タッチ防止のため
    // スライダー領域は初期 disabled。 ユーザーが明示的にタップ/Enter したら activated = true
    // になり、 透過オーバーレイが消えて操作可能になる。
    const activated = Vue.ref(false);
    function activate() {
      activated.value = true;
      // activate 直後にフォーカスを slider 内ハンドルへ移してキーボード操作も継続可能に
      Vue.nextTick(() => {
        const target = sub.value === 'bloodpressure' ? bpSliderRef.value : singleSliderRef.value;
        if (!target) return;
        const handle = target.querySelector('.noUi-handle');
        if (handle && handle.focus) {
          try { handle.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
        }
      });
    }

    const i18n = window.i18nStore;
    const tr = (s) => (i18n ? i18n.i18n(s) : s);
    const displayValue = Vue.computed(() => {
      const v = props.field.value;
      if (sub.value === "painscale") return v < 0 ? tr("未選択") : `${v} / 10`;
      if (sub.value === "bodytemp") return v == 0 ? "—" : `${(v / 10).toFixed(1)} ℃`;
      if (typeof v === "number") return `${v}${tr(props.field.unit || "")}`;
      return v ? tr(v) : "—";
    });

    function painscaleBoxStyle(n) {
      const startPct = 5.4;
      const endPct = 91.4;
      const stepPct = (endPct - startPct) / 10;
      const center = startPct + n * stepPct;
      const boxWidth = n === 10 ? 9.5 : 8.5;
      return { left: `${center - boxWidth / 2}%`, width: `${boxWidth}%` };
    }

    // ===== シングルモード (painscale / bodytemp / 通常 scale) =====
    const singleSliderRef = Vue.ref(null);
    let singleSlider = null;
    let suppressSingle = false;

    function getSingleConfig() {
      if (sub.value === "painscale") {
        return { min: -1, max: 10, step: 1, defaultStart: -1 };
      }
      if (sub.value === "bodytemp") {
        return { min: props.field.min ?? 340, max: props.field.max ?? 410, step: 1, defaultStart: 365 };
      }
      const min = props.field.min ?? 0;
      const max = props.field.max ?? 100;
      return { min, max, step: props.field.step ?? 1, defaultStart: min };
    }

    function initSingleSlider() {
      if (sub.value === "bloodpressure") return;
      if (!singleSliderRef.value || !window.noUiSlider || singleSlider) return;
      const cfg = getSingleConfig();
      const v = props.field.value;
      // 値が range 内なら使う、 外れる場合は defaultStart にフォールバック (内部値はそのまま)
      const start = typeof v === "number" && v >= cfg.min && v <= cfg.max ? v : cfg.defaultStart;

      singleSlider = window.noUiSlider.create(singleSliderRef.value, {
        start: [start],
        connect: "lower",
        range: { min: cfg.min, max: cfg.max },
        step: cfg.step,
        format: { to: (n) => Math.round(n), from: (s) => Number(s) },
        handleAttributes: [{ "aria-label": props.field.dispname || "スライダー" }]
      });

      singleSlider.on("update", (values) => {
        const newV = Number(values[0]);
        if (props.field.value !== newV) {
          suppressSingle = true;
          props.field.value = newV;
        }
      });

      // bodytemp の初期値 0 (未測定) や 通常 scale の初期 undefined は表示のみ整え、
      // 内部値はそのままにする
      if (typeof v !== "number" || v < cfg.min || v > cfg.max) {
        suppressSingle = true;
        // 触ったタイミングで update で書き込まれる
      }
    }

    function destroySingleSlider() {
      if (singleSlider) {
        try { singleSlider.destroy(); } catch (e) { /* ignore */ }
        singleSlider = null;
      }
    }

    Vue.watch(singleSliderRef, (el) => {
      if (!el) return;
      if (sub.value !== "bloodpressure" && window.noUiSlider && !singleSlider) {
        initSingleSlider();
      }
    }, { flush: "post", immediate: true });

    // 外部からの field.value 変更を slider に反映
    Vue.watch(
      () => (sub.value !== "bloodpressure" && typeof props.field.value === "number") ? props.field.value : null,
      (v) => {
        if (v === null || !singleSlider) return;
        if (suppressSingle) { suppressSingle = false; return; }
        const cur = Number(singleSlider.get());
        const cfg = getSingleConfig();
        const clamped = Math.max(cfg.min, Math.min(cfg.max, v));
        if (cur !== clamped) singleSlider.set([clamped]);
      }
    );

    // ===== デュアルモード (bloodpressure) =====
    const bpSliderRef = Vue.ref(null);
    let bpSlider = null;
    let suppressBp = false;

    function initBpSlider() {
      if (sub.value !== "bloodpressure") return;
      if (!bpSliderRef.value || !window.noUiSlider || bpSlider) return;

      const v = props.field.value || { lower: 0, upper: 0 };
      const startLower = v.lower > 0 ? v.lower : 80;
      const startUpper = v.upper > 0 ? v.upper : 120;

      bpSlider = window.noUiSlider.create(bpSliderRef.value, {
        start: [startLower, startUpper],
        connect: true,
        range: { min: 40, max: 240 },
        step: 1,
        margin: 10,
        handleAttributes: [
          { "aria-label": "下 (最低血圧)" },
          { "aria-label": "上 (最高血圧)" }
        ],
        format: { to: (n) => Math.round(n), from: (s) => Number(s) }
      });

      bpSlider.on("update", (values) => {
        const lo = Number(values[0]);
        const hi = Number(values[1]);
        if (props.field.value.lower !== lo || props.field.value.upper !== hi) {
          suppressBp = true;
          props.field.value.lower = lo;
          props.field.value.upper = hi;
        }
      });

      // 初期 0/0 (未入力) なら内部値を 0/0 に維持
      if (v.lower === 0 && v.upper === 0) {
        suppressBp = true;
        props.field.value.lower = 0;
        props.field.value.upper = 0;
      }
    }

    function destroyBpSlider() {
      if (bpSlider) {
        try { bpSlider.destroy(); } catch (e) { /* ignore */ }
        bpSlider = null;
      }
    }

    Vue.watch(bpSliderRef, (el) => {
      if (!el) return;
      if (sub.value === "bloodpressure" && window.noUiSlider && !bpSlider) {
        initBpSlider();
      }
    }, { flush: "post", immediate: true });

    Vue.watch(
      () => sub.value === "bloodpressure" && props.field.value
        ? [props.field.value.lower, props.field.value.upper] : null,
      (vals) => {
        if (!vals || !bpSlider) return;
        if (suppressBp) { suppressBp = false; return; }
        const cur = bpSlider.get();
        const lo = vals[0] > 0 ? vals[0] : 80;
        const hi = vals[1] > 0 ? vals[1] : 120;
        if (Number(cur[0]) !== lo || Number(cur[1]) !== hi) {
          bpSlider.set([lo, hi]);
        }
      }
    );

    Vue.onBeforeUnmount(() => {
      destroySingleSlider();
      destroyBpSlider();
    });

    return {
      sub,
      displayValue,
      painscaleBoxStyle,
      singleSliderRef,
      bpSliderRef,
      activated,
      activate
    };
  }
};
</script>
